import { useToast } from "@/hooks/use-toast";
import { useState } from "react";
import { useQueryClient, useMutation, useQuery } from "@tanstack/react-query";
import type { BuyDeal } from "@shared/schema";
import { apiRequest, parseErrorMessage } from "@/lib/queryClient";
import { Card } from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Loader2, Pencil, Plus, Trash2 } from "lucide-react";
import { Skeleton } from "@/components/ui/skeleton";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { BuyDealFormState } from "./types";
import { EMPTY_BUY_DEAL_FORM } from "./constants";
import { dealToForm, formToPayload } from "./helpers";

export function SampleBuyDealsPanel({ canManage = true }: { canManage?: boolean } = {}) {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const [showOnlySamples, setShowOnlySamples] = useState(true);
  const [editorOpen, setEditorOpen] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [form, setForm] = useState<BuyDealFormState>(EMPTY_BUY_DEAL_FORM);

  const dealsQuery = useQuery<BuyDeal[]>({
    queryKey: ["/api/admin/buy-deals"],
    queryFn: async () => {
      const res = await fetch("/api/admin/buy-deals", { credentials: "include" });
      if (!res.ok) throw new Error(`${res.status}: ${await res.text()}`);
      return res.json();
    },
    staleTime: 10_000,
  });

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ["/api/admin/buy-deals"] });
    queryClient.invalidateQueries({ queryKey: ["/api/app/buy-deals"] });
  };

  const createMutation = useMutation({
    mutationFn: async (payload: Record<string, unknown>) =>
      apiRequest("POST", "/api/admin/buy-deals", payload),
    onSuccess: () => {
      invalidate();
      toast({ title: "Sample deal created" });
      setEditorOpen(false);
    },
    onError: (e) => toast({ title: "Failed to create deal", description: parseErrorMessage(e, "Couldn't create deal. Please try again."), variant: "destructive" }),
  });

  const updateMutation = useMutation({
    mutationFn: async ({ id, payload }: { id: number; payload: Record<string, unknown> }) =>
      apiRequest("PATCH", `/api/admin/buy-deals/${id}`, payload),
    onSuccess: () => {
      invalidate();
      toast({ title: "Deal updated" });
      setEditorOpen(false);
    },
    onError: (e) => toast({ title: "Failed to update deal", description: parseErrorMessage(e, "Couldn't update deal. Please try again."), variant: "destructive" }),
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: number) => apiRequest("DELETE", `/api/admin/buy-deals/${id}`),
    onSuccess: () => {
      invalidate();
      toast({ title: "Deal deleted" });
    },
    onError: (e) => toast({ title: "Failed to delete deal", description: parseErrorMessage(e, "Couldn't delete deal. Please try again."), variant: "destructive" }),
  });

  const toggleSampleMutation = useMutation({
    mutationFn: async ({ id, isSample }: { id: number; isSample: boolean }) =>
      apiRequest("PATCH", `/api/admin/buy-deals/${id}`, { isSample }),
    onSuccess: () => {
      invalidate();
      toast({ title: "Sample status updated" });
    },
    onError: (e) => toast({ title: "Failed to update sample status", description: parseErrorMessage(e, "Couldn't update sample status. Please try again."), variant: "destructive" }),
  });

  const allDeals = dealsQuery.data ?? [];
  const visibleDeals = showOnlySamples ? allDeals.filter((d) => d.isSample) : allDeals;

  const openCreate = () => {
    setEditingId(null);
    setForm({ ...EMPTY_BUY_DEAL_FORM });
    setEditorOpen(true);
  };

  const openEdit = (deal: BuyDeal) => {
    setEditingId(deal.id);
    setForm(dealToForm(deal));
    setEditorOpen(true);
  };

  const handleSave = () => {
    if (!form.address.trim() || !form.city.trim() || !form.state.trim() || !form.market.trim()) {
      toast({ title: "Missing required fields", description: "Address, City, State, and Market are required.", variant: "destructive" });
      return;
    }
    if (!form.purchasePrice.trim() || !form.cashOnCash.trim() || !form.monthlyCashFlow.trim()) {
      toast({ title: "Missing required fields", description: "Purchase Price, Cash-on-Cash, and Monthly Cash Flow are required.", variant: "destructive" });
      return;
    }
    const payload = formToPayload(form);
    if (editingId == null) createMutation.mutate(payload);
    else updateMutation.mutate({ id: editingId, payload });
  };

  const [dealToDelete, setDealToDelete] = useState<BuyDeal | null>(null);

  const handleDelete = (deal: BuyDeal) => {
    setDealToDelete(deal);
  };

  const setField = <K extends keyof BuyDealFormState>(key: K, value: BuyDealFormState[K]) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  return (
    <Card className="p-6">
      <div className="flex items-start justify-between mb-4 gap-4 flex-wrap">
        <div>
          <h2 className="text-lg font-semibold">Sample Buy Deals</h2>
          <p className="text-sm text-muted-foreground">
            Curate the showcase deals shown on the Buy tab. Mark any deal as a sample, edit its details, or remove it without database access.
          </p>
        </div>
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-2">
            <Switch
              id="filter-only-samples"
              checked={showOnlySamples}
              onCheckedChange={(v) => setShowOnlySamples(!!v)}
              data-testid="switch-only-samples"
            />
            <Label htmlFor="filter-only-samples" className="text-sm">Show only samples</Label>
          </div>
          {canManage && (
            <Button onClick={openCreate} data-testid="button-create-sample-deal">
              <Plus className="h-4 w-4 mr-1" /> New Sample Deal
            </Button>
          )}
        </div>
      </div>

      {dealsQuery.isLoading ? (
        <div className="space-y-2">
          {[1, 2, 3].map((i) => (
            <Skeleton key={i} className="h-12 w-full" />
          ))}
        </div>
      ) : visibleDeals.length === 0 ? (
        <div className="text-sm text-muted-foreground py-12 text-center">
          {showOnlySamples ? "No sample deals yet. Click \"New Sample Deal\" to create one." : "No buy deals found."}
        </div>
      ) : (
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Address</TableHead>
                <TableHead>Market</TableHead>
                <TableHead>Price</TableHead>
                <TableHead>CoC %</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Sample</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {visibleDeals.map((deal) => (
                <TableRow key={deal.id} data-testid={`row-buy-deal-${deal.id}`}>
                  <TableCell className="font-medium">
                    <div>{deal.address}</div>
                    <div className="text-xs text-muted-foreground">{deal.city}, {deal.state}{deal.zipCode ? ` ${deal.zipCode}` : ""}</div>
                  </TableCell>
                  <TableCell>{deal.market}</TableCell>
                  <TableCell>${Number(deal.purchasePrice ?? 0).toLocaleString()}</TableCell>
                  <TableCell>{deal.cashOnCash}%</TableCell>
                  <TableCell><Badge variant="outline">{deal.status}</Badge></TableCell>
                  <TableCell>
                    <Switch
                      checked={!!deal.isSample}
                      disabled={!canManage}
                      onCheckedChange={(v) => toggleSampleMutation.mutate({ id: deal.id, isSample: !!v })}
                      data-testid={`switch-sample-${deal.id}`}
                    />
                  </TableCell>
                  <TableCell className="text-right">
                    {canManage ? (
                      <div className="flex justify-end gap-2">
                        <Button size="sm" variant="outline" onClick={() => openEdit(deal)} data-testid={`button-edit-deal-${deal.id}`}>
                          <Pencil className="h-3.5 w-3.5" />
                        </Button>
                        <Button size="sm" variant="outline" onClick={() => handleDelete(deal)} data-testid={`button-delete-deal-${deal.id}`}>
                          <Trash2 className="h-3.5 w-3.5" />
                        </Button>
                      </div>
                    ) : (
                      <span className="text-xs text-muted-foreground">View only</span>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      <Dialog open={editorOpen} onOpenChange={setEditorOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingId == null ? "Create Sample Buy Deal" : "Edit Buy Deal"}</DialogTitle>
            <DialogDescription>
              Sample deals appear on the Buy tab with a "SAMPLE" watermark. Toggle "Mark as sample" to control visibility.
            </DialogDescription>
          </DialogHeader>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div className="md:col-span-2 flex items-center gap-2 p-3 rounded-md bg-muted/40">
              <Switch id="form-is-sample" checked={form.isSample} onCheckedChange={(v) => setField("isSample", !!v)} data-testid="switch-form-is-sample" />
              <Label htmlFor="form-is-sample">Mark as sample (show on Buy tab as a curated showcase deal)</Label>
            </div>

            <div>
              <Label>Address *</Label>
              <Input value={form.address} onChange={(e) => setField("address", e.target.value)} data-testid="input-address" />
            </div>
            <div>
              <Label>Market *</Label>
              <Input value={form.market} onChange={(e) => setField("market", e.target.value)} placeholder="Atlanta" data-testid="input-market" />
            </div>
            <div>
              <Label>City *</Label>
              <Input value={form.city} onChange={(e) => setField("city", e.target.value)} data-testid="input-city" />
            </div>
            <div>
              <Label>State *</Label>
              <Input value={form.state} onChange={(e) => setField("state", e.target.value)} data-testid="input-state" />
            </div>
            <div>
              <Label>Zip Code</Label>
              <Input value={form.zipCode} onChange={(e) => setField("zipCode", e.target.value)} data-testid="input-zip" />
            </div>
            <div>
              <Label>Status</Label>
              <Input value={form.status} onChange={(e) => setField("status", e.target.value)} placeholder="preview" data-testid="input-status" />
            </div>

            <div>
              <Label>Purchase Price *</Label>
              <Input value={form.purchasePrice} onChange={(e) => setField("purchasePrice", e.target.value)} placeholder="225000" data-testid="input-purchase-price" />
            </div>
            <div>
              <Label>Cash-on-Cash % *</Label>
              <Input value={form.cashOnCash} onChange={(e) => setField("cashOnCash", e.target.value)} placeholder="18" data-testid="input-coc" />
            </div>
            <div>
              <Label>Monthly Cash Flow *</Label>
              <Input value={form.monthlyCashFlow} onChange={(e) => setField("monthlyCashFlow", e.target.value)} placeholder="850" data-testid="input-monthly-cf" />
            </div>
            <div>
              <Label>Cap Rate</Label>
              <Input value={form.capRate} onChange={(e) => setField("capRate", e.target.value)} placeholder="7.5" data-testid="input-cap-rate" />
            </div>

            <div>
              <Label>Current Bedrooms *</Label>
              <Input type="number" value={form.currentBedrooms} onChange={(e) => setField("currentBedrooms", e.target.value)} data-testid="input-current-bedrooms" />
            </div>
            <div>
              <Label>Current Bathrooms *</Label>
              <Input type="number" value={form.currentBathrooms} onChange={(e) => setField("currentBathrooms", e.target.value)} data-testid="input-current-bathrooms" />
            </div>
            <div>
              <Label>Future Bedrooms</Label>
              <Input type="number" value={form.futureBedrooms} onChange={(e) => setField("futureBedrooms", e.target.value)} data-testid="input-future-bedrooms" />
            </div>
            <div>
              <Label>Future Bathrooms</Label>
              <Input type="number" value={form.futureBathrooms} onChange={(e) => setField("futureBathrooms", e.target.value)} data-testid="input-future-bathrooms" />
            </div>

            <div>
              <Label>Sqft</Label>
              <Input type="number" value={form.sqft} onChange={(e) => setField("sqft", e.target.value)} data-testid="input-sqft" />
            </div>
            <div>
              <Label>Year Built</Label>
              <Input type="number" value={form.yearBuilt} onChange={(e) => setField("yearBuilt", e.target.value)} data-testid="input-year-built" />
            </div>
            <div>
              <Label>Property Type</Label>
              <Input value={form.propertyType} onChange={(e) => setField("propertyType", e.target.value)} placeholder="Single Family" data-testid="input-property-type" />
            </div>
            <div>
              <Label>ARV</Label>
              <Input value={form.arv} onChange={(e) => setField("arv", e.target.value)} data-testid="input-arv" />
            </div>
            <div>
              <Label>Rehab Cost</Label>
              <Input value={form.rehabCost} onChange={(e) => setField("rehabCost", e.target.value)} data-testid="input-rehab" />
            </div>
            <div>
              <Label>Monthly Rent</Label>
              <Input value={form.monthlyRent} onChange={(e) => setField("monthlyRent", e.target.value)} data-testid="input-monthly-rent" />
            </div>
            <div>
              <Label>Monthly Expenses</Label>
              <Input value={form.monthlyExpenses} onChange={(e) => setField("monthlyExpenses", e.target.value)} data-testid="input-monthly-expenses" />
            </div>
            <div>
              <Label>Listing URL</Label>
              <Input value={form.listingUrl} onChange={(e) => setField("listingUrl", e.target.value)} data-testid="input-listing-url" />
            </div>

            <div className="md:col-span-2">
              <Label>Cover Image URL</Label>
              <Input value={form.imageUrl} onChange={(e) => setField("imageUrl", e.target.value)} placeholder="https://…" data-testid="input-image-url" />
            </div>
            <div className="md:col-span-2">
              <Label>Photos (one URL per line)</Label>
              <Textarea
                value={form.photos}
                onChange={(e) => setField("photos", e.target.value)}
                rows={4}
                placeholder={"https://example.com/a.jpg\nhttps://example.com/b.jpg"}
                data-testid="textarea-photos"
              />
            </div>
            <div className="md:col-span-2">
              <Label>Notes</Label>
              <Textarea value={form.notes} onChange={(e) => setField("notes", e.target.value)} rows={3} data-testid="textarea-notes" />
            </div>
          </div>

          <div className="flex justify-end gap-2 mt-4">
            <Button variant="outline" onClick={() => setEditorOpen(false)} data-testid="button-cancel-deal">Cancel</Button>
            <Button
              onClick={handleSave}
              disabled={createMutation.isPending || updateMutation.isPending}
              data-testid="button-save-deal"
            >
              {(createMutation.isPending || updateMutation.isPending) && <Loader2 className="h-4 w-4 mr-1 animate-spin" />}
              {editingId == null ? "Create Deal" : "Save Changes"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
      <AlertDialog open={!!dealToDelete} onOpenChange={(v) => !v && setDealToDelete(null)}>
        <AlertDialogContent data-testid="dialog-delete-deal-confirm">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete deal?</AlertDialogTitle>
            <AlertDialogDescription>
              {dealToDelete ? `Delete deal "${dealToDelete.address}"? This cannot be undone.` : "This cannot be undone."}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="button-cancel-delete-deal">Cancel</AlertDialogCancel>
            <AlertDialogAction
              data-testid="button-confirm-delete-deal"
              disabled={deleteMutation.isPending}
              onClick={(e) => {
                e.preventDefault();
                if (!dealToDelete) return;
                const id = dealToDelete.id;
                deleteMutation.mutate(id, {
                  onSettled: () => setDealToDelete(null),
                });
              }}
              className="bg-red-600 hover:bg-red-700 focus:ring-red-600"
            >
              {deleteMutation.isPending ? "Deleting..." : "Delete"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Card>
  );
}
